import classNames from 'classnames';
import { MouseEventHandler, useCallback, useMemo } from 'react';
import { Button, Figure, Heading, List } from 'yoso';
import { Badge } from '~/components/Badge/Badge';
import { BaseCard } from '~/components/Card';
import { useChallengeBoost } from '~/components/Challenge/Boost/ChallengeBoost.hooks';
import { myCitizenItemsUrl } from '~/components/Dosi/Dosi.hooks';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { Translation } from '~/components/I18n';
import tbdCat from '~/components/Icon/cat.png';
import { levelMetaMap } from '~/components/Level/Level.hooks';
import { BottomSheet, BottomSheetProps } from '~/components/Modal/BottomSheet';
import { format, formatBoost } from '~/components/Util/number';
import styles from './ChallengeBoostBottomSheet.module.scss';

export type ChallengeBoostBottomSheetProps = BottomSheetProps;

export const ChallengeBoostBottomSheet = ({
  className,
  onConfirm,
  onDismiss,
  ...props
}: ChallengeBoostBottomSheetProps) => {
  const { open } = useEmbeddedViewer();

  const { data, isInitialLoading } = useChallengeBoost();
  const { citizenBoostDetailList, petBoostDetailList, petBoost, totalBoost } = data;
  const friendCount = useMemo(
    () => petBoostDetailList.reduce((accumulator, { count }) => accumulator + count, 0),
    [petBoostDetailList]
  );

  const handleClick = useCallback<MouseEventHandler>(
    (e) => {
      open(myCitizenItemsUrl);
      onConfirm?.(e);
    },
    [onConfirm, open]
  );

  return (
    <BottomSheet
      className={classNames(styles.root, className)}
      onConfirm={onConfirm}
      onDismiss={onDismiss}
      {...props}>
      <Heading level={4} className={styles.heading}>
        <Translation
          name="citizen_boost_status_title"
          options={[
            <span key={0} className={styles.highlight}>
              {formatBoost(totalBoost, { zeroAsEmpty: isInitialLoading })}%
            </span>,
          ]}
        />
      </Heading>
      <p className={styles.description}>
        <Translation name="citizen_boost_status_desc" />
      </p>
      <List className={styles.list}>
        {citizenBoostDetailList.map(({ level, count, boost }) => (
          <li key={level}>
            <BaseCard className={styles.card} size="large">
              <Figure className={styles.cardImage} src={levelMetaMap[level].placeholderImage} />
              <div className={styles.cardBody}>
                <Heading level={5} className={styles.cardTitle}>
                  Citizen Lv{level}
                </Heading>
                <p className={styles.cardDescription}>
                  <Translation
                    name="citizen_boost_status_quantity"
                    options={[format(count, { zeroAsEmpty: isInitialLoading })]}
                  />
                </p>
              </div>
              <Badge color="white" variant="pill" className={styles.cardBadge}>
                {formatBoost(boost, { zeroAsEmpty: isInitialLoading })}%
              </Badge>
            </BaseCard>
          </li>
        ))}
        <li>
          <BaseCard className={styles.card}>
            <Figure className={styles.cardImage} src={tbdCat} />
            <div className={styles.cardBody}>
              <Heading level={5} className={styles.cardTitle}>
                Friends
              </Heading>
              <p className={styles.cardDescription}>
                <Translation
                  name="citizen_boost_status_quantity"
                  options={[format(friendCount, { zeroAsEmpty: isInitialLoading })]}
                />
              </p>
            </div>
            <Badge color="white" variant="pill" className={styles.cardBadge}>
              {formatBoost(petBoost, { zeroAsEmpty: isInitialLoading })}%
            </Badge>
          </BaseCard>
        </li>
      </List>
      <Button variant="outlined" block className={styles.button} onClick={handleClick}>
        <Translation name="citizen_boost_status_item_btn" />
      </Button>
    </BottomSheet>
  );
};
